import React, { createContext, useContext, useMemo, useState } from "react";
import {
  addItemToBatchV2,
  cancelBatch,
  confirmPackingGroups,
  confirmPlacementOption,
  confirmPlacementOptionLTL,
  confirmTransportationOption,
  createBatchV2,
  generatePlacementOptionsLTL,
  submitItems,
} from "@/api/fulfillment";
import { useErrorModalContext } from "./ErrorModalContext";
import {
  ASINPrepInstructions,
  AutoFillListPriceFrom,
  AutoFillListPriceOperation,
  BoxContentsProvider,
  BxError,
  CartonItem,
  CreateInboundBatchResponse,
  ErrorCode,
  FbaBatchStatus,
  FBABoxDTO,
  FBAIncentive,
  FBAItem,
  FbaShipmentStatus,
  FulfillmentType,
  InboundBatch,
  ISO8601Date,
  LabelOwner,
  LabelPrepPreference,
  LabelPrepType,
  MskuPrepDetail,
  PackingOptionDTO,
  PackingType,
  PalletBody,
  PalletResponse,
  PlacementOptionDTO,
  PlacementShipmentOptionDTO,
  PrepCategory,
  PrepOwner,
  ProductCondition,
  SyncJobStatus,
  TransportationOption,
  TransportType,
} from "@boxem/core-api-schema";
import { useLocation } from "wouter";
import { Address } from "./UserContext";
import { logRocketError } from "@/utils/logUtils";
import { getBatchStep } from "@/app/fba/utils";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import { CloneShipmentModal } from "@/app/fba/CloneShipmentModal/CloneShipmentModal";
import dayjs from "dayjs";


export class FbaBox {
  id?: number;

  width?: number;
  height?: number;
  length?: number;

  weight?: number;

  items: CartonItem[] = []; // @TODO remove

  displayIndex: number;
  trackingId?: string;

  // v2:
  shipmentId?: number; // in v1, shipments had boxes. now, boxes start on the batch, and we later determine which box goes to which shipment with this id.
  packageId?: string;
  packingGroupId?: string;
  packingItems: { batchItemId: number, quantity: number, sku: string }[] = []; // @TODO rename 'items'

  constructor(serializedModel?: FBABoxDTO) {
    if (serializedModel) {
      Object.assign(this, serializedModel);
      this.width = Number(this.width);
      this.height = Number(this.height);
      this.length = Number(this.length);
      this.weight = Number(this.weight);
    }
  }
}

export type FbaShipmentFromAddress = {
  address1?: string;
  address2?: string;
  city?: string;
  company?: string;
  country?: string;
  email?: string;
  fromSync?: boolean;
  id?: number;
  name?: string;
  phone?: string;
  state?: string;
  type?: string;
  userId?: number;
  zipcode?: string;
};

export type FbaShipmentPatch = {
  shipmentName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  freightClass?: string;
  freightReadyDate?: string;
  declaredValue?: number;
  pallets?: PalletBody[];
};

export class FbaShipment {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  last_updated: Date;
  shipmentId: string;
  shipmentName: string;
  fulfillmentCenterId: string;
  destinationAddress: Address;
  labelPrepType: LabelPrepType;
  cartonContentError?: string;
  transportSubmissionError?: string;
  estimateAmount: number = 0;
  estimateCurrency: string = "USD";
  name?: string;
  photo?: string;

  status: FbaShipmentStatus;
  batchStatus: FbaBatchStatus | null;
  fromId: number; // from address id
  items: FbaShipmentItem[] = [];
  from?: FbaShipmentFromAddress;
  inboundShipmentId?: string;

  contact?: {
    id?: number,
    name: string,
    email: string,
    phone: string
  } = {
    name: "",
    email: "",
    phone: ""
  };
  freightClass?: string;
  declaredValue: number = 0;
  declaredValueCurrency: string = "USD";
  freightReadyDate: string;
  voidDeadline: Date;
  transportType: TransportType = TransportType.SPD;
  boxContentsProvider: BoxContentsProvider;

  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;

  boxes: FbaBox[] = []; // @NOTE(nick) don't use this when you care about the items inside the box!
                        // look-up the items inside the box definition in the containing batch by
                        // finding the box with a matching id!
  pallets: PalletResponse[] = [];
  transportationOptions: TransportationOption[] = [];
  bolUrl: string;
  bolNumber?: string;

  constructor(serializedModel: any, batch?: FbaBatch) {
    Object.assign(this, serializedModel);

    // serializedModel.boxes will be undefined for steps <= 2
    this.boxes = serializedModel?.boxes?.map((box) => new FbaBox(box)).sort((a, b) => a.displayIndex - b.displayIndex) ?? [];

    // @HACK - we return the destination address as a json string from the backend (it's stored as one too)
    // It's also not the type 'Address', even though we have this table!! .. we'll convert it into an 'Address'
    // here, but really, we should just be provided it...
    if (serializedModel.destinationAddress) {
      // console.count(typeof serializedModel.destinationAddress); // @TODO why do we get an object in one case...
      try {
        const destination = JSON.parse(serializedModel.destinationAddress);
        this.destinationAddress = {
          name: destination.Name,
          address1: destination.AddressLine1,
          address2: destination.AddressLine2,
          city: destination.City,
          state: destination.StateOrProvinceCode,
          country: destination.CountryCode,
          zipcode: destination.PostalCode
        };
      } catch (e) {
        try {
          // @HACK
          const destination = this.destinationAddress as any;
          this.destinationAddress = {
            name: destination.Name,
            address1: destination.AddressLine1,
            address2: destination.AddressLine2,
            city: destination.City,
            state: destination.StateOrProvinceCode,
            country: destination.CountryCode,
            zipcode: destination.PostalCode
          };
        } catch (e) {
          logRocketError(`Failed to JSON.parse a destination address on shipment ${this.id}, ${JSON.stringify(serializedModel.destinationAddress)}, ${typeof serializedModel.destinationAddress}`);
        }
      }
    }

    if (serializedModel.voidDeadline) {
      this.voidDeadline = new Date(serializedModel.voidDeadline);
    }

    if (batch) {
      this.transportType = batch.shipmentType;
      this.batchStatus = batch.status;
      this.items = serializedModel.items.map((item) => {
        const batchItem = batch.items.find((batchItem) => batchItem.id === item.fbaItemId);
        item.batchItem = batchItem;
        return item;
      });
      this.boxContentsProvider = batch.boxContentsProvider;
    } else {
      this.batchStatus = null;
      this.items = serializedModel.items.map((item: FbaShipmentItem) => {
        return item;
      })
    }
    if (serializedModel.createdAt) {
      this.createdAt = new Date(serializedModel.createdAt);
    }
  }
}

// @TODO remove in v2:
export type FbaShipmentItem = {
  id: number;
  batchItem: FbaBatchItem;
  fbaItemId: number;
  quantity: number;
  quantityAssigned: number;
  quantityReceived: number;
  prepDetails: any;
  asin: string;
  sku: string;
  fnSku: string;
  condition: ProductCondition;
  name?: string;
  photo?: string;
  expirationDate?: string;
  image?: string; // stores image url of deleted items
}

export class FbaBatchItem {
  id: number;
  name: string;
  image: string;
  listPrice: number;
  asin: string;
  sku: string;
  category?: string;
  condition: ProductCondition;
  conditionNotes: string;
  buyCost: number;
  datePurchased?: string;
  expirationDate?: string;
  supplierId?: number;
  quantity: number;

  // new in v2:
  labelOwner?: LabelOwner;
  prepOwner?: PrepOwner;

  // @NOTE this only exists on the frontend, temporarily during step 1.
  // retrieving details about an item's prep post step 1 will be on the items within the packing group.
  prepCategoryPollingStatus: SyncJobStatus = SyncJobStatus.COMPLETED;
  prepDetails?: MskuPrepDetail;
  prepCategory?: PrepCategory; 

  createdAt: Date; // @TODO remove in v2 (unused)
  fnSku: string; // @TODO remove in v2 (is on packing group)
  quantityInCase: number = 1; // @TODO remove in v2
  prepInstructions?: ASINPrepInstructions; // @TODO remove in v2

  constructor(serializedModel: any) {
    Object.assign(this, serializedModel);

    if (serializedModel.listing?.prepInstructions) {
      this.prepInstructions = JSON.parse(serializedModel.listing.prepInstructions);
    }
    if (serializedModel.listPrice) {
      this.listPrice = +serializedModel.listPrice;
    }
  }
}

export interface ProductBoxDetails {
  quantity: number;
  boxIndex: number;
  boxId: number;
  boxDisplayIndex: number;
}

export class PlacementOption {
  status: "OFFERED" | "ACCEPTED" | "EXPIRED";
  discounts: FBAIncentive[];
  expiration?: ISO8601Date;
  fees: FBAIncentive[];
  placementOptionId: string;
  shipmentOptions: PlacementShipmentOptionDTO[];
  isExpired?: boolean;

  constructor(serializedModel: PlacementOptionDTO) {
    if (serializedModel) {
      Object.assign(this, serializedModel);

      if (this.expiration) {
        this.isExpired = new Date(this.expiration) < new Date();
      }
    }
  }
}

export interface PlacementOptionFees {
  // on the placement option itself:
  placement?: number;
  discount?: number;

  // on the transportation option:
  transportation?: number;

  // on the packing groups within the placement option:
  prep?: number;

  total?: number; // everything together before discounts
}

export type PackingOption = PackingOptionDTO;

export class FbaBatch {
  version: string = "2";
  id: number;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  fromId: number;

  placementOptionsStatusesFromIncompleteBatchesDTO?: PlacementOption["status"][];

  packingType: PackingType = PackingType.INDIVIDUAL;
  labelPrepPreference: LabelPrepPreference = LabelPrepPreference.SELLER_LABEL;
  boxContentsProvider: BoxContentsProvider = BoxContentsProvider.Boxem;
  shipmentType: TransportType = TransportType.SPD;
  printBoxLabelsStep3: boolean = false;
  autoFillListPrice: boolean = false;
  autoFillListPriceOperation: AutoFillListPriceOperation = AutoFillListPriceOperation.INCREASE;
  autoFillListPriceFrom: AutoFillListPriceFrom = AutoFillListPriceFrom.BUY_BOX;
  autoFillListPriceAmount: string;
  showSupplierInput: boolean = false;
  showBuyCostInput: boolean = false;
  showDatePurchasedInput: boolean = false;
  status: FbaBatchStatus;
  items: FbaBatchItem[] = [];
  shipments: FbaShipment[] = [];
  printFNSKUAfterIncreaseQuantityInShipment: boolean;
  printFNSKUAfterAssigningToBox: boolean;
  printFNSKUAfterAddingToShipment: boolean;
  printFNSKUAfterIncreaseQuantityInBox: boolean;

  // v2 stuff:
  readyToShip: string; // iso date
  packingOptions: PackingOption[] = [];
  chosenPackingOption?: PackingOption;
  boxes: FbaBox[] = [];
  placementOptions: PlacementOption[] = [];
  confirmedPlacementOption?: PlacementOption;
  // assigned item quantities got more complex with v2, because now we only care about quantities within a specific packing group.
  // the key here is packingGroupId + sku. Lookup with function 'getItemAssignedQuantity'.
  itemAssignedQuantities?: { [key: string]: number } = {};
  productBoxDetails?: { [key: string]: ProductBoxDetails[] } = {};

  // this is ephemereal/not stored on the backend, we just set it when switching between
  // placement options in step 2/3, so the context can know about it. on refresh it's lost
  // if you are in the state where you have not yet 'confirmed' a placement option,
  // and then we auto-select the first placement option.
  selectedPlacementOptionId?: string;

  // key is placement option id:
  fees: { [key: string]: PlacementOptionFees } = {};

  constructor(serializedModel?: InboundBatch|CreateInboundBatchResponse|FbaBatch) { // @TODO use DTO v2
    if (serializedModel) {
      Object.assign(this, serializedModel);

      if ("shipments" in serializedModel) {
        this.shipments = serializedModel.shipments.map((shipment) => new FbaShipment(shipment, this));
        this.shipments.sort((a, b) => a.fulfillmentCenterId.localeCompare(b.fulfillmentCenterId)); // to ensure consistent display order.
      }

      if ("items" in serializedModel) {
        this.items = serializedModel.items.map(item => new FbaBatchItem(item));
      }

      // v2 below here:
      if ("boxes" in serializedModel) {
        this.boxes = serializedModel.boxes.map((box) => new FbaBox(box as FBABoxDTO)); // @TODO DTO v2
      }

      if ("packingOptions" in serializedModel) {
        // I don't know if the SPAPI will be reliable at providing us packing options in order,
        // and SQL also doesn't provide garuntees about return order in general, so we'll use the
        // same strategy where we sort the array client-side to ensure consistent presentation order.
        // this is important because we use the index as an identifier in some cases (to determine 
        // packing group color, for example).
        this.packingOptions.sort((a, b) => a.packingOptionId.localeCompare(b.packingOptionId));
        this.chosenPackingOption = this.packingOptions.find(po => po.status === "ACCEPTED");
        this.chosenPackingOption?.packingGroups.sort((a, b) => a.inboundPackingGroupId.localeCompare(b.inboundPackingGroupId));
      }

      if ("placementOptions" in serializedModel) {
        // unlike packing options, we don't care about reliability of presentation order of these, probably.
        this.placementOptions = serializedModel.placementOptions.map(po => new PlacementOption(po));
        this.confirmedPlacementOption = this.placementOptions.find(po => po.status === "ACCEPTED");

        // bit of a hack here, FBA shipments want a 'destinationAddress' field, but in v2 we don't store it there,
        // it's on the ShipmentOption corresponding to the shipment. so we will do the fix-up now...
        for (const placementOption of this.placementOptions) {
          for (const shipmentOption of placementOption.shipmentOptions) {
            const shipment = this.shipments.find(s => s.inboundShipmentId === shipmentOption.shipmentId);
            if (!shipment) continue;
            const a = shipmentOption.fulfillmentCenter.address;
            shipment.destinationAddress = {
              name: a.name,
              address1: a.addressLine1,
              address2: a.addressLine2,
              city: a.city,
              state: a.stateOrProvinceCode,
              zipcode: a.postalCode,
              company: a.companyName,
            }
          }
        }
      }
    }

    recalculateBatchLookupTables(this);
  }
}

export function itemAssignedQuantityKey(packingGroupId: string|null, shipmentId: number|null, msku: string): string { // v2 function
  return `${shipmentId ?? packingGroupId}${msku}`;
}

export function getItemAssignedQuantity(batch: FbaBatch, packingGroupId: string|null, shipmentId: number|null, msku: string): number { // v2 function
  return batch?.itemAssignedQuantities[itemAssignedQuantityKey(packingGroupId, shipmentId, msku)] ?? 0;
}

export function getItemBoxDetails(batch: FbaBatch, packingGroupId: string|null, shipmentId: number|null, msku: string): ProductBoxDetails[] {
  const key = shipmentId ? shipmentId.toString() + msku : packingGroupId + msku;
  return batch?.productBoxDetails[key] ?? [];
}

export function getFbaBatchItemById(batch: FbaBatch, batchItemId: number): FbaBatchItem {
  return batch.items.find(i => i.id === batchItemId);
}

export function getPackingGroupItem(batch: FbaBatch, packingGroupId: string, sku: string): FBAItem {
  return batch.chosenPackingOption?.packingGroups.find(pg => pg.inboundPackingGroupId === packingGroupId)
    ?.items.find(i => i.msku === sku);
}

const recalculateBatchLookupTables = (batch: FbaBatch) => {
  if (!batch) return; // can happen when nulling the current batch.

  batch.shipments.forEach((shipment) => {
    shipment.items.forEach((item) => {
      item.quantityAssigned = 0;
      shipment.boxes.forEach((box) => {
        const itemInBox = box.items.find((i) => i.shipmentItemId === item.id);
        if (itemInBox) item.quantityAssigned += itemInBox.quantity;
      });
    });
  });

  // v2:
  batch.itemAssignedQuantities = {};

  const offsetAssignedQuantity = (map: { [key: string]: number }, packingGroupId: string, shipmentId: number, sku: string, offset: number) => {
    const key = shipmentId ? shipmentId.toString() + sku : packingGroupId + sku;
    if (!map[key]) {
      map[key] = offset;
    } else {
      map[key] += offset;
    }
  }

  if (batch.shipmentType === TransportType.SPD) {
    batch.chosenPackingOption?.packingGroups?.forEach(pg => {
      pg.items.forEach(item => {
        batch.boxes.forEach(box => {
          if (box.packingGroupId === pg.inboundPackingGroupId) {
            // the box is in this pack group, so if the item is also in the box,
            // it will count towards the assigned quantity of the item within the
            // packing group.
            const batchItem = batch.items.find(i => i.sku === item.msku);
            const itemInBox = box.packingItems.find(i => i.batchItemId === batchItem?.id);
            if (itemInBox) offsetAssignedQuantity(batch.itemAssignedQuantities, pg.inboundPackingGroupId, null, item.msku, itemInBox.quantity);
          }
        });
      });
    });
  }
  else if (batch.shipmentType === TransportType.LTL) {
    batch.shipments?.forEach(shipment => {
      shipment.items.forEach(item => {
        batch.boxes.forEach(box => {
          if (box.shipmentId === shipment.id) {
            // the box is in this shipment, so if the item is also in the box,
            // it will count towards the assigned quantity of the item within the
            // shipment.
            const batchItem = batch.items.find(i => i.sku === item.sku);
            const itemInBox = box.packingItems.find(i => i.batchItemId === batchItem?.id);
            if (itemInBox) offsetAssignedQuantity(batch.itemAssignedQuantities, null, shipment.id, item.sku, itemInBox.quantity);
          }
        });
      });
    });
  }

  // key are packing group id + SKUs (same as assigned quantities). 
  // lookup a SKU and it tells you which boxes, if any, the SKU is in, and the relevant quantities.
  batch.productBoxDetails = {}; 
  batch.boxes.forEach((box, boxIndex) => {
    box.packingItems.forEach((packingItem, packingItemIndex) => {
      const batchItem = batch.items.find(i => i.id === packingItem.batchItemId);
      if (!batchItem) return; // <-- can happen for v1 batches.
      if (batch.shipmentType === TransportType.LTL && !box.shipmentId) {
        console.error("box with empty ship id: ", box.id);
      }
      const key = batch.shipmentType === TransportType.SPD 
        ? box.packingGroupId + batchItem.sku 
          : box.shipmentId?.toString() + batchItem.sku;

      if (batch.productBoxDetails[key]) {
        batch.productBoxDetails[key].push({
          quantity: packingItem.quantity,
          boxIndex: boxIndex,
          boxId: box.id,
          boxDisplayIndex: box.displayIndex,
        });
      } else {
        batch.productBoxDetails[key] = [
          {
            quantity: packingItem.quantity,
            boxIndex: boxIndex,
            boxId: box.id,
            boxDisplayIndex: box.displayIndex,
          },
        ];
      }
    });
  });

  batch.fees = {};
  if (batch.chosenPackingOption) {
    for (const placementOption of batch.placementOptions ?? []) {
      
      const placementTotal = placementOption.fees?.reduce((acc, curr) => acc + curr.value.amount, 0) ?? 0;
      const discountTotal = placementOption.discounts?.reduce((acc, curr) => acc + curr.value.amount, 0) ?? 0;
      let prepTotal = batch.chosenPackingOption.fees?.reduce((acc, curr) => acc + curr.value.amount, 0);

      let transportTotal = 0;
      placementOption.shipmentOptions.forEach((to, toIndex) => {
        const transportationOption = to.transportationOptions.find(option => option.carrier.name === "UPS");
        if (!transportationOption) return; // happens for v1, or if the option hasn't been fetched yet
        transportTotal += transportationOption.quote.cost.amount;
      });

      batch.fees[placementOption.placementOptionId] = {
        placement: placementTotal,
        discount: discountTotal,
        transportation: transportTotal,
        prep: prepTotal,
        total: placementTotal + transportTotal + prepTotal,
      }
    }

    // sort placement options by estimate fee totals:
    batch.placementOptions?.sort((a, b) => batch.fees[a.placementOptionId].total - batch.fees[b.placementOptionId].total);
  }
};

type ShipmentContextProps = {
  createShipment: () => void;
  currentBatch: FbaBatch;
  setCurrentBatch: (batch: FbaBatch | null) => void;

  selectedTransportationOptions: Record<number, string>;
  setSelectedTransportationOptions: (selectedTransportationOptions: Record<number, string>) => void;

  advanceShipmentFlowStep: () => Promise<void>;

  openCloneShipmentConfirmModal: (focusedShipmentId?: number) => void;
  cloneShipment: (doCancelBatch: boolean, useFocusedShipmentSKUsOnlyId?: number | null) => Promise<FbaBatch>;
};

const ShipmentContext = createContext<ShipmentContextProps>(undefined);
export const useShipmentContext = () => useContext(ShipmentContext);

const globalBatch = {
  batch: {} as FbaBatch,
};

export const ShipmentContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [location, navigate] = useLocation();

  const { handleErrorResponseGenerically } = useErrorModalContext();

  const [currentBatch, setCurrentBatch] = useState<FbaBatch | null>(null);

  const [betweenStepsErrors, setBetweenStepsErrors] = useState<BxError[]>([]);

  const [cloneShipmentModalOpen, setCloneShipmentModalOpen] = useState<boolean>(false);
  const [cloneShipmentId, setCloneShipmentId] = useState<number>(null);

  const [selectedTransportationOptions, setSelectedTransportationOptions] = useState<Record<number, string>>(null);
  const createShipment = () => navigate("~/fba/shipments/create");

  // creates a new shipment with items from the source batch
  const cloneShipment: (doCancelBatch: boolean, useFocusedShipmentSKUsOnlyId: number) => Promise<FbaBatch|undefined>
    = async (doCancelBatch, useFocusedShipmentSKUsOnlyId = null) => {
    try {
      const newBatch = await createBatchV2({
        name: ("Clone of: " + currentBatch.name).substring(0, 40),
        fromAddressId: currentBatch.fromId,
        fulfillmentType: FulfillmentType.FBA,
        packingType: PackingType.INDIVIDUAL,
        labelPrepPreference: LabelPrepPreference.SELLER_LABEL,
        shipmentType: currentBatch.shipmentType,
        boxContentsProvider: currentBatch.boxContentsProvider,
        printBoxLabelsStep3: false,
        autoFillListPrice: currentBatch.autoFillListPrice,
        autoFillListPriceAmount: currentBatch.autoFillListPriceAmount,
        autoFillListPriceFrom: currentBatch.autoFillListPriceFrom,
        autoFillListPriceOperation: currentBatch.autoFillListPriceOperation,
        showSupplierInput: currentBatch.showSupplierInput,
        showBuyCostInput: currentBatch.showBuyCostInput,
        showDatePurchasedInput: currentBatch.showDatePurchasedInput,
        printFNSKUAfterAddingToShipment: currentBatch.printFNSKUAfterAddingToShipment ?? false,
        printFNSKUAfterIncreaseQuantityInShipment: currentBatch.printFNSKUAfterIncreaseQuantityInShipment ?? false,
        printFNSKUAfterAssigningToBox: currentBatch.printFNSKUAfterAssigningToBox ?? false,
        printFNSKUAfterIncreaseQuantityInBox: currentBatch.printFNSKUAfterIncreaseQuantityInBox ?? false,
        readyToShip: dayjs().add(1, "day").toISOString(), // default value is tomorrow
      });
      const newBatchItemRequests: Promise<FbaBatchItem>[] = [];
      if (useFocusedShipmentSKUsOnlyId != null && currentBatch.shipments.find(s => s.id === useFocusedShipmentSKUsOnlyId)) {
        const shipment = currentBatch.shipments.find(s => s.id === useFocusedShipmentSKUsOnlyId);
        for (const shipmentItem of shipment.items) {
          const item = currentBatch.items.find(i => i.id === shipmentItem.fbaItemId);
  
          // if you pass '0' you can get: PostgresError: insert or update on table "fba_item" violates foreign key constraint "fba_item_supplierId_supplier_id_fk"
          if (!item.supplierId || item.supplierId === 0) delete item.supplierId;

          newBatchItemRequests.push(addItemToBatchV2(newBatch.id, {
            asin: item.asin,
            sku: item.sku,
            name: item.name,
            listPrice: item.listPrice?.toString(),
            quantity: shipmentItem.quantity,
            condition: item.condition,
            conditionNotes: item.conditionNotes,
            buyCost: item.buyCost?.toString(),
            supplierId: item.supplierId, 
            datePurchased: item.datePurchased,
            expirationDate: item.expirationDate,
            category: item.category,
            labelOwner: item.labelOwner,
            prepOwner: item.prepOwner,
          }));
        }
      } else {
        for (const item of currentBatch?.items) {
  
          // if you pass '0' you can get: PostgresError: insert or update on table "fba_item" violates foreign key constraint "fba_item_supplierId_supplier_id_fk"
          if (!item.supplierId || item.supplierId === 0) delete item.supplierId;

          newBatchItemRequests.push(addItemToBatchV2(newBatch.id, {
            asin: item.asin,
            sku: item.sku,
            name: item.name,
            listPrice: item.listPrice?.toString(),
            quantity: item.quantity,
            condition: item.condition,
            conditionNotes: item.conditionNotes,
            buyCost: item.buyCost?.toString(),
            supplierId: item.supplierId, 
            datePurchased: item.datePurchased,
            expirationDate: item.expirationDate,
            category: item.category,
            labelOwner: item.labelOwner,
            prepOwner: item.prepOwner,
          }));
        }
      }

      const newBatchItems = await Promise.all(newBatchItemRequests);
      for (const newItem of newBatchItems) {
        newBatch.items.push(newItem);
      }
      if (doCancelBatch) {
        const result = await cancelBatch(currentBatch.id);
      }
      _setCurrentBatch(newBatch);
      return newBatch;

    } catch (e) {
      console.error(e);
      handleErrorResponseGenerically(e);
    }
  }

  const _setCurrentBatch = (batch: FbaBatch) => {
    if (batch) { 
      recalculateBatchLookupTables(batch);
    }
    setCurrentBatch(batch);
    globalBatch.batch = batch;
  };

  const advanceShipmentFlowStep = async () => {
    if (!currentBatch) {
      return;
    }

    const stepNumber = getBatchStep(currentBatch);

    switch (stepNumber) {
      case 0: {
        for await (const result of submitItems(currentBatch.id)) {
          if (result.errors) {
            if (handleErrorResponseGenerically(result.errors)) return;
  
            setBetweenStepsErrors(result.errors);
            return;
          }

          if (result.data) {
            currentBatch.status = result.data.batchStatus;
            currentBatch.packingOptions = result.data.packingOptions;
            currentBatch.chosenPackingOption = currentBatch.packingOptions?.[0];
            _setCurrentBatch({...currentBatch});

            if (currentBatch.chosenPackingOption) {
              break;
            }
          }
 
          if (result.keepPolling) {
            continue;
          }
        }

        // in LTL we also need to generate placement options before moving to step 2:
        if (currentBatch.shipmentType === TransportType.LTL) {
          for await (const placementOptionsResult of generatePlacementOptionsLTL(currentBatch.id)) {
            if (placementOptionsResult.errors) {
              if (handleErrorResponseGenerically(placementOptionsResult.errors)) return;
            }
  
            if (placementOptionsResult.data) {
              currentBatch.placementOptions = placementOptionsResult.data.placementOptions?.map(po => new PlacementOption(po));
              currentBatch.status = placementOptionsResult.data.status;
              _setCurrentBatch({...currentBatch});

              if (currentBatch.placementOptions) {
                break;
              }
            }
          }
        }
      } break;

      case 1: {
        if (currentBatch.shipmentType === TransportType.SPD) {
          for await (const submitBoxesResult of confirmPackingGroups(currentBatch.id)) {
            if (submitBoxesResult.errors) {
              handleErrorResponseGenerically(submitBoxesResult.errors);
              return;
            }

            if (submitBoxesResult.data?.status) {
              currentBatch.status = submitBoxesResult.data.status;

              if (submitBoxesResult.data?.boxes && submitBoxesResult.data?.placementOptions) {
                currentBatch.placementOptions = submitBoxesResult.data.placementOptions.map(po => new PlacementOption(po));
                currentBatch.boxes = submitBoxesResult.data.boxes.map(b => new FbaBox(b));
              }

              _setCurrentBatch({...currentBatch});

              if (submitBoxesResult.data?.boxes && submitBoxesResult.data?.placementOptions) {
                return;
              }
            }
          }
        } else if (currentBatch.shipmentType === TransportType.LTL) {
          const result = await confirmPlacementOptionLTL(currentBatch.selectedPlacementOptionId);
          if (result.errors) {
            handleErrorResponseGenerically(result.errors);
            return;
          }

          if (result.data.status) {
            const po = currentBatch.placementOptions.find(po => po.placementOptionId === result.data.placementOptionId);
            po.status = result.data.status;
            currentBatch.status = result.data.batchStatus;
            currentBatch.shipments = result.data.shipments.map(s => new FbaShipment(s));
            _setCurrentBatch({...currentBatch});
          }
        }
      } break;

      case 2: {
        if (currentBatch.shipmentType === TransportType.SPD) {
          for await (const confirmPlacementOptionResult of confirmPlacementOption(currentBatch.selectedPlacementOptionId, currentBatch.id)) {
            if (confirmPlacementOptionResult.errors) {
              handleErrorResponseGenerically(confirmPlacementOptionResult.errors);
              return;
            }

            if (confirmPlacementOptionResult.data?.batchStatus) {
              currentBatch.status = confirmPlacementOptionResult.data.batchStatus;

              if (confirmPlacementOptionResult.data.placementOptionId) {
                const placementOption = currentBatch.placementOptions.find(po => po.placementOptionId === confirmPlacementOptionResult.data.placementOptionId);
                if (placementOption) {
                  placementOption.status = confirmPlacementOptionResult.data.status;
                }
                currentBatch.boxes = confirmPlacementOptionResult.data.boxes.map(b => new FbaBox(b));
                currentBatch.shipments = confirmPlacementOptionResult.data.shipments.map(s => new FbaShipment(s, currentBatch));
              }

              _setCurrentBatch({...currentBatch});

              if (confirmPlacementOptionResult.data.placementOptionId) {
                return;
              }
            }
          }
        }
        else if (currentBatch.shipmentType === TransportType.LTL) {
          for await (const submitBoxesResult of confirmPackingGroups(currentBatch.id)) {
            if (submitBoxesResult.errors) {
              handleErrorResponseGenerically(submitBoxesResult.errors);
              return;
            }

            if (submitBoxesResult.data?.status) {
              currentBatch.status = submitBoxesResult.data.status;

              if (submitBoxesResult.data?.boxes && submitBoxesResult.data?.placementOptions) {
                currentBatch.confirmedPlacementOption = submitBoxesResult.data.placementOptions[0];
                //currentBatch.boxes = submitBoxesResult.data.boxes.map(b => new FbaBox(b));
                currentBatch.shipments.forEach(s => {
                  const shipment = submitBoxesResult.data.placementOptions[0].shipmentOptions.find(o => o.id === s.id);
                  if (shipment) {
                    s.transportationOptions = shipment.transportationOptions;
                  }
                  else {
                    console.error('transportation option shipment not matched: ', s.id);
                  }
                })
              }

              _setCurrentBatch({...currentBatch});

              if (submitBoxesResult.data?.boxes && submitBoxesResult.data?.placementOptions) {
                return;
              }
            }
          }
        }
      } break;

      case 3:
        if (currentBatch.shipmentType === TransportType.LTL) {
          // confirm transportation options            
          for await (const result of confirmTransportationOption(currentBatch.confirmedPlacementOption.placementOptionId, Object.values(selectedTransportationOptions))) {
            if (result.errors || result[0]?.message) {
              handleErrorResponseGenerically(result.errors ?? result[0]);
              return;
            }

            if (!result.keepPolling) {
              if (result.data) {
                currentBatch.status = FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRMED;
                result.data.forEach(shipmentData => {
                  const shipment = currentBatch.shipments.find(s => s.id === shipmentData.id);
                  if (shipment) {
                    //shipment.boxes = shipmentData.boxes;
                    shipment.bolUrl = shipmentData.bolS3Url;
                    shipment.bolNumber = shipmentData.bolNumber;
                  }
                });
                _setCurrentBatch({...currentBatch});
              }
            }
          } 
        } 
        break;
    }
  };

  return (
    <ShipmentContext.Provider
      value={{
        currentBatch,
        setCurrentBatch: _setCurrentBatch,
        selectedTransportationOptions,
        setSelectedTransportationOptions,

        createShipment,

        advanceShipmentFlowStep,

        openCloneShipmentConfirmModal: (focusedShipmentId?: number) => {
          setCloneShipmentId(focusedShipmentId);
          setCloneShipmentModalOpen(true);
        },

        cloneShipment,
      }}
    >
      <>
        <CloneShipmentModal
          open={cloneShipmentModalOpen}
          onClose={() => setCloneShipmentModalOpen(false)}
          focusedShipmentId={cloneShipmentId}
        />

        <ConfirmModal
          open={betweenStepsErrors.length > 0}
          onClose={() => setBetweenStepsErrors([])}
          title={"Errors Encountered"}
          body={betweenStepsErrors.map((error, i) => {
            switch (error.type) {
              case ErrorCode.SKU_DOES_NOT_REQUIRE_PREP_OWNER_BUT_SELLER_OR_AMAZON_WAS_ASSIGNED:
                return (
                  <p key={i}>
                    It looks like the SKU: {error.details} requires its prep owner and label owner be set to 'None'. Edit the prep and label owner(s) by clicking on it in the right-hand panel, then try again.
                  </p>
                );

              // case ErrorCode.SELLER_ACCOUNT_MIGHT_HAVE_INVALID_CHARGE_METHOD:
              //   break;

              case ErrorCode.ADD_TO_BATCH_ITEM_MISSING_EXPIRATION_DATE:
                return (
                  <p key={i}>It looks like the SKU: {error.details} requires an expiration date, but none was provided. Edit the expiration date by clicking on it in the right-hand panel, then try again.</p>
                );
          }})}
        />
        {children}
      </>
    </ShipmentContext.Provider>
  );
};
